import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import {
    Trophy,
    Check,
    X,
    RotateCcw,
    ArrowRight,
    HelpCircle,
    GraduationCap,
    Clock,
    ShieldCheck,
    AlertCircle,
    Lock
} from 'lucide-react';
import { updateStreak } from '../../utils/streak';
import { logActivity } from '../../utils/activity';
import { checkAndAwardBadges, Badge } from '../../utils/badges';
import {
    Star,
    Zap,
    Medal
} from 'lucide-react';
import { hasExamAccess } from '../../utils/subscription';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Exam() {
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
    const [showResult, setShowResult] = useState(false);
    const [score, setScore] = useState(0);
    const [showFinalScore, setShowFinalScore] = useState(false);
    const [inExam, setInExam] = useState(false);
    const [timer, setTimer] = useState(1800); // 30 minutes in seconds
    const [showConfirmAbort, setShowConfirmAbort] = useState(false);
    const [newBadges, setNewBadges] = useState<Badge[]>([]);
    const [showBadgeModal, setShowBadgeModal] = useState(false);
    const [subscription, setSubscription] = useState<any>(null);

    useEffect(() => {
        checkUser();
    }, []);

    useEffect(() => {
        let interval: any;
        if (inExam && timer > 0 && !showFinalScore) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0 && !showFinalScore) {
            setShowFinalScore(true);
        }
        return () => clearInterval(interval);
    }, [inExam, timer, showFinalScore]);

    const checkUser = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                window.location.href = '/login';
                return;
            }
            setUser(user);

            const { data: sub } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .eq('status', 'active')
                .single();
            setSubscription(sub);

            setLoading(false);
        } catch (error) {
            console.error('Erreur:', error);
            window.location.href = '/login';
        }
    };

    const startExam = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('question_exam')
                .select('*');

            if (error) throw error;

            const shuffled = (data || []).sort(() => Math.random() - 0.5).slice(0, 20);
            setQuestions(shuffled);
            setInExam(true);
            setTimer(1800);
            setCurrentIndex(0);
            setSelectedAnswer(null);
            setShowResult(false);
            setScore(0);
            setShowFinalScore(false);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitAnswer = () => {
        if (selectedAnswer === null) return;
        const currentQuestion = questions[currentIndex];
        const isCorrect = selectedAnswer === currentQuestion.correct_index;

        if (isCorrect) setScore(score + 1);
        setShowResult(true);
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setSelectedAnswer(null);
            setShowResult(false);
        } else {
            finishExam();
        }
    };

    const finishExam = async () => {
        setShowFinalScore(true);
        await updateStreak();
        logActivity({
            type: 'quiz',
            title: t('exam.title'),
            subtitle: t('exam.active.submit'),
            value: `${score}/${questions.length}`,
        });

        // Badge checking
        if (user) {
            try {
                // For exams, we treat it as a general activity
                // We don't have a specific category, but we can still trigger first_quiz and perfect_score
                const awarded = await checkAndAwardBadges(user.id, 0, {
                    score,
                    total: questions.length,
                    progressPercent: 0,
                    totalThemesCompleted: 0
                });

                if (awarded.length > 0) {
                    setNewBadges(awarded);
                    setShowBadgeModal(true);
                }
            } catch (error) {
                console.error('Error checking badges for exam:', error);
            }
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading && !inExam) {
        return (
            <DashboardLayout user={user}>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    const isPremium = hasExamAccess(subscription);

    return (
        <DashboardLayout user={user}>
            <SEO noindex={true} />
            <div className="max-w-4xl mx-auto pb-20">
                {!isPremium ? (
                    <div className="bg-dark-card rounded-3xl border border-dark-border p-12 text-center relative overflow-hidden animate-in zoom-in duration-500">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
                        <div className="w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-8 text-primary shadow-lg shadow-primary/10">
                            <Lock size={48} />
                        </div>
                        <h1 className="text-3xl font-bold text-text-main mb-4">{t('exam.locked.title')}</h1>
                        <p className="text-text-muted mb-10 max-w-lg mx-auto leading-relaxed whitespace-pre-line">
                            {t('exam.locked.description')}
                        </p>

                        <Link
                            to="/pricing"
                            className="inline-flex items-center justify-center px-12 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20 scale-105 hover:scale-110 gap-2"
                        >
                            <span>{t('exam.locked.cta')}</span>
                            <ArrowRight size={20} />
                        </Link>
                    </div>
                ) : !inExam ? (
                    <div className="bg-dark-card rounded-3xl border border-dark-border p-12 text-center">
                        <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-8 text-primary">
                            <GraduationCap size={40} />
                        </div>
                        <h1 className="text-3xl font-bold text-text-main mb-4">{t('exam.ready.title')}</h1>
                        <p className="text-text-muted mb-8 max-w-lg mx-auto leading-relaxed">
                            {t('exam.ready.description')}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                                <Clock className="text-primary mx-auto mb-2" size={24} />
                                <p className="text-sm font-bold text-text-main">{t('exam.ready.stats.time')}</p>
                                <p className="text-[10px] text-text-muted uppercase font-bold tracking-wider">{t('exam.ready.stats.time_label')}</p>
                            </div>
                            <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                                <HelpCircle className="text-primary mx-auto mb-2" size={24} />
                                <p className="text-sm font-bold text-text-main">{t('exam.ready.stats.questions')}</p>
                                <p className="text-[10px] text-text-muted uppercase font-bold tracking-wider">{t('exam.ready.stats.questions_label')}</p>
                            </div>
                            <div className="p-4 bg-dark-bg rounded-2xl border border-dark-border">
                                <ShieldCheck className="text-primary mx-auto mb-2" size={24} />
                                <p className="text-sm font-bold text-text-main">{t('exam.ready.stats.passing')}</p>
                                <p className="text-[10px] text-text-muted uppercase font-bold tracking-wider">{t('exam.ready.stats.passing_label')}</p>
                            </div>
                        </div>

                        <button
                            onClick={startExam}
                            className="px-12 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20 scale-105 hover:scale-110"
                        >
                            {t('exam.ready.cta')}
                        </button>
                    </div>
                ) : !showFinalScore ? (
                    <div>
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2 px-4 py-2 bg-dark-card rounded-xl border border-dark-border">
                                    <Clock className={timer < 300 ? 'text-red-500 animate-pulse' : 'text-primary'} size={18} />
                                    <span className={`font-mono font-bold ${timer < 300 ? 'text-red-500' : 'text-text-main'}`}>
                                        {formatTime(timer)}
                                    </span>
                                </div>
                                <span className="px-3 py-1 bg-dark-card rounded-lg border border-dark-border text-xs text-text-muted">
                                    {t('exam.active.question_count', { current: currentIndex + 1, total: questions.length })}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowConfirmAbort(true)}
                                className="text-text-muted hover:text-red-500 transition-colors text-sm font-medium"
                            >
                                {t('exam.active.quit')}
                            </button>
                        </div>

                        <div className="w-full h-1.5 bg-dark-border/30 rounded-full mb-8 overflow-hidden">
                            <div
                                className="h-full bg-primary transition-all duration-300 ease-out"
                                style={{ width: `${((currentIndex) / questions.length) * 100}%` }}
                            />
                        </div>

                        <div className="bg-dark-card rounded-3xl border border-dark-border p-8 mb-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-8 opacity-5">
                                <GraduationCap size={120} />
                            </div>

                            <h3 className="text-2xl font-bold text-text-main mb-8 leading-relaxed relative z-10">
                                {(i18n.language === 'en' ? questions[currentIndex]?.question_en : questions[currentIndex]?.question) || questions[currentIndex]?.question}
                            </h3>

                            <div className="space-y-3 relative z-10">
                                {['option_a', 'option_b', 'option_c', 'option_d'].map((optionKey, index) => {
                                    const isSelected = selectedAnswer === index;
                                    const isCorrect = questions[currentIndex].correct_index === index;
                                    const optionKeyEn = `${optionKey}_en`;
                                    const optionText = (i18n.language === 'en' ? questions[currentIndex][optionKeyEn] : questions[currentIndex][optionKey]) || questions[currentIndex][optionKey];

                                    return (
                                        <button
                                            key={index}
                                            disabled={showResult}
                                            onClick={() => setSelectedAnswer(index)}
                                            className={`w-full p-5 rounded-2xl border text-left transition-all flex items-center justify-between group ${showResult && isCorrect
                                                ? 'border-green-500 bg-green-500/10'
                                                : showResult && isSelected && !isCorrect
                                                    ? 'border-red-500 bg-red-500/10'
                                                    : isSelected
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-dark-border bg-dark-bg hover:border-primary/30'
                                                }`}
                                        >
                                            <span className={`font-medium ${showResult && isCorrect ? 'text-green-500 font-bold' :
                                                showResult && isSelected && !isCorrect ? 'text-red-500' :
                                                    isSelected ? 'text-text-main' : 'text-text-muted'
                                                }`}>
                                                {optionText}
                                            </span>
                                            {showResult && isCorrect && <Check className="text-green-500" size={20} />}
                                            {showResult && isSelected && !isCorrect && <X className="text-red-500" size={20} />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            {!showResult ? (
                                <button
                                    onClick={handleSubmitAnswer}
                                    disabled={selectedAnswer === null}
                                    className="px-10 py-3 bg-primary text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-hover transition-all shadow-lg shadow-primary/20"
                                >
                                    {t('exam.active.submit')}
                                </button>
                            ) : (
                                <button
                                    onClick={handleNext}
                                    className="px-10 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
                                >
                                    {currentIndex === questions.length - 1 ? t('exam.active.view_results') : t('exam.active.next')}
                                    <ArrowRight size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-dark-card rounded-3xl border border-dark-border p-12 text-center animate-in zoom-in duration-500">
                        <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 ${score >= 15 ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                            {score >= 15 ? <Trophy size={48} /> : <X size={48} />}
                        </div>

                        <h2 className="text-4xl font-bold text-text-main mb-2">
                            {score >= 15 ? t('exam.result.win') : t('exam.result.lose')}
                        </h2>
                        <p className={`text-xl font-bold mb-8 ${score >= 15 ? 'text-green-500' : 'text-red-500'}`}>
                            {score >= 15 ? t('exam.result.score_win') : t('exam.result.score_lose')}
                        </p>

                        <div className="grid grid-cols-2 gap-8 mb-12 max-w-sm mx-auto">
                            <div className="text-center p-6 bg-dark-bg rounded-2xl border border-dark-border">
                                <p className="text-4xl font-black text-primary mb-1">{score}/20</p>
                                <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest">{t('exam.result.stats.final_score')}</p>
                            </div>
                            <div className="text-center p-6 bg-dark-bg rounded-2xl border border-dark-border">
                                <p className="text-4xl font-black text-text-main mb-1">{Math.round((score / questions.length) * 100)}%</p>
                                <p className="text-text-muted text-[10px] font-bold uppercase tracking-widest">{t('exam.result.stats.accuracy')}</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-4">
                            <button
                                onClick={startExam}
                                className="w-full py-4 bg-primary text-white font-bold rounded-2xl hover:bg-primary-hover transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/25"
                            >
                                <RotateCcw size={20} /> {t('exam.result.retry')}
                            </button>
                            <button
                                onClick={() => setInExam(false)}
                                className="w-full py-4 bg-dark-hover text-text-main font-bold rounded-2xl border border-dark-border hover:bg-dark-bg transition-all"
                            >
                                {t('exam.result.back_home')}
                            </button>
                        </div>
                    </div>
                )
                }
            </div >

            {/* Custom Abort Confirmation Modal */}
            {
                showConfirmAbort && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-dark-bg/80 backdrop-blur-sm animate-in fade-in duration-300">
                        <div className="bg-dark-card border border-dark-border rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500 mx-auto mb-6">
                                <AlertCircle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-text-main text-center mb-2">{t('exam.abort.title')}</h3>
                            <p className="text-text-muted text-center mb-8">
                                {t('exam.abort.description')}
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowConfirmAbort(false)}
                                    className="flex-1 py-3 px-4 rounded-xl bg-dark-hover text-text-main font-bold border border-dark-border hover:bg-dark-bg transition-all"
                                >
                                    {t('exam.abort.continue')}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmAbort(false);
                                        setInExam(false);
                                    }}
                                    className="flex-1 py-3 px-4 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-all shadow-lg shadow-red-500/20"
                                >
                                    {t('exam.abort.quit')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Badge Celebration Modal */}
            {
                showBadgeModal && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-xl animate-in fade-in duration-500">
                        <div className="bg-dark-card border border-primary/30 rounded-[2.5rem] p-10 max-w-lg w-full shadow-[0_0_50px_rgba(220,38,38,0.3)] text-center relative overflow-hidden animate-in zoom-in duration-500">
                            {/* Decorative background */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
                            <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
                            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />

                            <div className="relative z-10">
                                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/20 animate-bounce">
                                    <Trophy size={48} className="text-white" />
                                </div>

                                <h2 className="text-3xl font-black text-white mb-2 tracking-tight">{t('exam.badges.new')}</h2>
                                <p className="text-primary font-bold uppercase tracking-widest text-sm mb-8">{t('exam.badges.subtitle')}</p>

                                <div className="space-y-4 mb-10">
                                    {newBadges.map((badge) => (
                                        <div key={badge.id} className="bg-dark-bg/50 border border-dark-border p-6 rounded-3xl flex items-center gap-5 group hover:border-primary/50 transition-all">
                                            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shrink-0 group-hover:scale-110 transition-transform">
                                                {badge.icon === 'Trophy' && <Trophy size={32} />}
                                                {badge.icon === 'Star' && <Star size={32} />}
                                                {badge.icon === 'Zap' && <Zap size={32} />}
                                                {badge.icon === 'GraduationCap' && <GraduationCap size={32} />}
                                                {badge.icon === 'Medal' && <Medal size={32} />}
                                                {badge.icon === 'ShieldCheck' && <ShieldCheck size={32} />}
                                            </div>
                                            <div className="text-left">
                                                <h3 className="text-lg font-bold text-text-main group-hover:text-primary transition-colors">
                                                    {(i18n.language === 'en' ? (badge.title_en || badge.title) : (badge.title)) || badge.title}
                                                </h3>
                                                <p className="text-sm text-text-muted leading-tight">
                                                    {(i18n.language === 'en' ? (badge.description_en || badge.description) : (badge.description)) || badge.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => setShowBadgeModal(false)}
                                    className="w-full py-4 bg-primary hover:bg-primary-hover text-white font-black rounded-2xl transition-all shadow-lg shadow-primary/25 active:scale-95 text-lg"
                                >
                                    {t('exam.badges.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </DashboardLayout >
    );
}
